// Users (admin) — registration IS creation here (invite-only): only emails in
// this list can complete Google sign-in. Curro redesign: role-chooser cards,
// tenant scope picked with source/campus toggle-chips, users table collapses
// to cards on mobile. Tenant users carry a scope (sources and/or campuses)
// that the server force-applies to every query they make.
// Data wiring (UsersApi) is unchanged from the previous version.

import { useEffect, useState } from "react";
import { UsersApi, AnalyticsApi, PipelineApi } from "../hooks/useApi.js";
import Icon from "../components/Icon.jsx";

const ROLES = [
  { id: "admin",  icon: "shield",   color: "var(--curro-blue)",   desc: "Full access + diagnostics" },
  { id: "client", icon: "globe",    color: "var(--curro-violet)", desc: "Whole network, read-only" },
  { id: "tenant", icon: "building", color: "var(--curro-green)",  desc: "Scoped to their campus(es)" },
];
const roleColor = Object.fromEntries(ROLES.map((r) => [r.id, r.color]));

const EMPTY = { email: "", displayName: "", role: "client", sourceKeys: [], entityIds: [] };

export default function Users() {
  const [users, setUsers] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [campuses, setCampuses] = useState([]);
  const [sources, setSources] = useState([]);
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);

  const load = () => UsersApi.list().then(setUsers).catch((e) => setError(e.message));
  useEffect(() => {
    load();
    AnalyticsApi.meta().then((m) => setCampuses(m.campuses ?? [])).catch(() => {});
    PipelineApi.sources().then((s) => setSources(s.map((x) => x.sourceKey))).catch(() => {});
  }, []);

  const toggle = (k, v) => setForm((f) => ({ ...f, [k]: f[k].includes(v) ? f[k].filter((x) => x !== v) : [...f[k], v] }));

  const scopeOf = (f) =>
    f.role === "tenant" ? { sourceKeys: f.sourceKeys, entityIds: f.entityIds } : null;

  const create = async () => {
    setError(null); setNotice(null);
    try {
      await UsersApi.create({ email: form.email, displayName: form.displayName || null, role: form.role, scope: scopeOf(form) });
      setNotice(`${form.email} registered as ${form.role}.`);
      setForm(EMPTY);
      setOpen(false);
      load();
    } catch (e) { setError(e.body?.error ?? e.message); }
  };

  const toggleStatus = async (u) => {
    setError(null);
    try {
      await UsersApi.update(u.id, { email: u.email, role: u.role, scope: u.scope, status: u.status === "active" ? "disabled" : "active" });
      load();
    } catch (e) { setError(e.body?.error ?? e.message); }
  };

  const remove = async (u) => {
    if (!window.confirm(`Remove ${u.email}? They will no longer be able to sign in.`)) return;
    try { await UsersApi.remove(u.id); load(); } catch (e) { setError(e.body?.error ?? e.message); }
  };

  const fmtLogin = (s) => (s ? s.slice(0, 16).replace("T", " · ") : "never");
  const scopeText = (u) =>
    u.role === "admin" ? "Everything"
      : u.role === "client" ? "Whole network"
      : u.scope
        ? [u.scope.sourceKeys?.join(", "), u.scope.entityIds?.join(", ")].filter(Boolean).join(" · ") || "all in source"
        : "all in source";

  return (
    <div className="rise">
      <div className="page-head">
        <div>
          <div className="eyebrow">Admin · Access</div>
          <h1>Users &amp; access</h1>
          <p className="lead">Invite-only: only registered, active emails can sign in. Admins see everything, clients see the
            whole network read-only, and campus users (tenants) are auto-scoped server-side to their own slice.</p>
        </div>
        <button type="button" className={`btn ${open ? "ghost" : "primary"} lg`} onClick={() => setOpen(!open)}>
          <Icon name={open ? "x" : "plus"} size={17} /> {open ? "Cancel" : "Invite user"}
        </button>
      </div>

      {open && (
        <div className="panel rise" style={{ borderTop: "3px solid var(--primary)" }}>
          <div className="eyebrow" style={{ marginBottom: 14 }}><Icon name="plus" size={13} /> Register a user</div>
          <div className="controls">
            <div className="field"><label>Email</label>
              <input type="email" value={form.email} placeholder="name@company.com" onChange={(e) => setForm({ ...form, email: e.target.value })} /></div>
            <div className="field"><label>Display name</label>
              <input type="text" value={form.displayName} placeholder="optional" onChange={(e) => setForm({ ...form, displayName: e.target.value })} /></div>
          </div>

          <div className="field" style={{ marginTop: 14 }}><label>Role</label>
            <div className="choice-grid" style={{ gridTemplateColumns: "repeat(3,1fr)" }}>
              {ROLES.map((r) => (
                <button type="button" key={r.id} className={`choice ${form.role === r.id ? "on" : ""}`} onClick={() => setForm({ ...form, role: r.id })}>
                  <div className="check"><Icon name="check" size={12} /></div>
                  <div className="ci" style={{ color: r.color }}><Icon name={r.icon} size={18} /></div>
                  <b style={{ textTransform: "capitalize" }}>{r.id}</b><small>{r.desc}</small>
                </button>
              ))}
            </div>
          </div>

          {form.role === "tenant" && (
            <div className="rise" style={{ marginTop: 14, display: "flex", flexDirection: "column", gap: 14 }}>
              <div className="fb-section">
                <div className="fb-label">Scope to sources <span className="fb-count">{form.sourceKeys.length || "all"}</span></div>
                <div className="chipset">
                  {sources.map((s) => {
                    const on = form.sourceKeys.includes(s);
                    return <button type="button" key={s} className={`tchip ${on ? "on" : ""}`} onClick={() => toggle("sourceKeys", s)}>{on && <Icon name="check" size={13} />}{s}</button>;
                  })}
                </div>
              </div>
              <div className="fb-section">
                <div className="fb-label">Scope to campuses <span className="fb-count">{form.entityIds.length || "all"}</span></div>
                <div className="chipset">
                  {campuses.map((c) => {
                    const on = form.entityIds.includes(c);
                    return <button type="button" key={c} className={`tchip ${on ? "on" : ""}`} onClick={() => toggle("entityIds", c)}>{on && <Icon name="check" size={13} />}{c}</button>;
                  })}
                </div>
                <p className="hint" style={{ marginTop: 4 }}>Leave empty to allow all campuses in their assigned source(s). Enforced on every query server-side.</p>
              </div>
            </div>
          )}

          <div style={{ marginTop: 16 }}>
            <button type="button" className="btn primary" disabled={!form.email} onClick={create}>
              <Icon name="check" size={15} /> Register {form.role}
            </button>
          </div>
        </div>
      )}

      {error && <div className="error"><Icon name="alert" size={16} /> {error}</div>}
      {notice && <div className="notice"><Icon name="check" size={16} /> {notice}</div>}

      <h2 className="section-title">Registered users <span className="count">{users.length}</span></h2>
      <div className="tbl-wrap m-cards-wrap">
        <table className="m-cards">
          <thead><tr><th>Person</th><th>Role</th><th>Scope</th><th>Status</th><th>Last login</th><th>Actions</th></tr></thead>
          <tbody>
            {users.map((u) => (
              <tr key={u.id}>
                <td>
                  <div style={{ display: "flex", alignItems: "center", gap: 11 }}>
                    <span style={{ width: 34, height: 34, borderRadius: "50%", display: "grid", placeItems: "center", color: "#fff", fontFamily: "var(--font-head)", fontWeight: 700, fontSize: 13, flex: "none", background: roleColor[u.role] ?? "var(--muted-2)" }}>
                      {(u.displayName || u.email)[0].toUpperCase()}
                    </span>
                    <div style={{ lineHeight: 1.25 }}>
                      <b style={{ color: "var(--ink)", display: "block" }}>{u.displayName ?? "—"}</b>
                      <span className="num" style={{ fontSize: 11.5, color: "var(--muted)" }}>{u.email}</span>
                    </div>
                  </div>
                </td>
                <td data-label="Role"><span className={`tag role-${u.role}`}>{u.role}</span></td>
                <td className="num hint" data-label="Scope">{scopeText(u)}</td>
                <td data-label="Status">
                  {u.status === "active"
                    ? <span className="chip ok"><Icon name="check" size={12} /> Active</span>
                    : <span className="chip muted"><Icon name="x" size={12} /> Disabled</span>}
                </td>
                <td className="num hint" data-label="Last login">{fmtLogin(u.lastLoginAt)}</td>
                <td data-label="Actions">
                  <div style={{ display: "flex", gap: 6, justifyContent: "flex-end" }}>
                    <button type="button" className="btn ghost sm" onClick={() => toggleStatus(u)}>{u.status === "active" ? "Disable" : "Enable"}</button>
                    <button type="button" className="btn danger sm" onClick={() => remove(u)} aria-label={`Remove ${u.email}`}><Icon name="trash" size={13} /></button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
